import { WarningFilled } from '@carbon/icons-react';
import { createContext, useCallback, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import s from './FormField.module.css';

export interface FormFieldContextValue {
  id: string;
  labelId: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
  /** Поле сообщает об ошибке формата (например, неполный телефон); FormField её показывает. */
  reportError: (message: string | null) => void;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

/** Данные поля для контрола внутри FormField; вне FormField возвращает null. */
export function useFormField(): FormFieldContextValue | null {
  return useContext(FormFieldContext);
}

export interface FormFieldProps {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  /** Текст ошибки: что не так и как исправить. */
  error?: ReactNode;
  required?: boolean;
  /** Показать пометку «необязательно» (когда большинство полей формы обязательны). */
  optional?: boolean;
  disabled?: boolean;
  id?: string;
  /** Скрыть подпись визуально (остаётся для экранных дикторов). */
  hideLabel?: boolean;
  /** Действие справа от подписи (например, ссылка). */
  labelAction?: ReactNode;
  className?: string;
}

/**
 * Поле формы: видимая подпись, подсказка и ошибка связаны с контролом
 * (`htmlFor`, `aria-describedby`, `aria-invalid`) — WCAG 1.3.1, 3.3.1, 3.3.2.
 */
export function FormField({ label, children, hint, error, required = false, optional = false, disabled = false, id, hideLabel = false, labelAction, className }: FormFieldProps) {
  const { t } = useTranslation('ui');
  const autoId = useId();
  const controlId = id ?? `field-${autoId}`;
  const labelId = `${controlId}-label`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const [internalError, setInternalError] = useState<string | null>(null);
  const shownError = error ?? internalError;
  const reportError = useCallback((message: string | null) => setInternalError(message), []);
  const describedBy = [hint ? hintId : null, shownError ? errorId : null].filter(Boolean).join(' ') || undefined;

  const value = useMemo<FormFieldContextValue>(
    () => ({ id: controlId, labelId, describedBy, invalid: Boolean(shownError), required, disabled, reportError }),
    [controlId, labelId, describedBy, shownError, required, disabled, reportError],
  );

  return (
    <div className={cx(s.field, disabled && s.disabled, className)} data-invalid={shownError ? 'true' : undefined}>
      <div className={cx(s.labelRow, hideLabel && 'visually-hidden')}>
        <label id={labelId} htmlFor={controlId} className={s.label}>
          {label}
          {required ? (
            <span className={s.required} aria-hidden="true">
              *
            </span>
          ) : null}
          {optional ? <span className={s.optional}>({t('optional')})</span> : null}
        </label>
        {labelAction}
      </div>
      <FormFieldContext.Provider value={value}>{children}</FormFieldContext.Provider>
      <div className={s.messages}>
        {hint ? (
          <p id={hintId} className={s.hint}>
            {hint}
          </p>
        ) : null}
        {shownError ? (
          <p id={errorId} className={s.error}>
            <WarningFilled size={16} aria-hidden="true" />
            <span>{shownError}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

export interface FieldsetProps {
  legend: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  hideLegend?: boolean;
}

/** Группа связанных контролов (радиокнопки, набор чекбоксов) с общей подписью. */
export function Fieldset({ legend, children, hint, error, className, hideLegend = false }: FieldsetProps) {
  const autoId = useId();
  const hintId = `fieldset-${autoId}-hint`;
  const errorId = `fieldset-${autoId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <fieldset className={cx(s.fieldset, className)} aria-describedby={describedBy} aria-invalid={error ? true : undefined}>
      <legend className={cx(s.legend, hideLegend && 'visually-hidden')}>{legend}</legend>
      {children}
      <div className={s.messages}>
        {hint ? (
          <p id={hintId} className={s.hint}>
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={errorId} className={s.error}>
            <WarningFilled size={16} aria-hidden="true" />
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </fieldset>
  );
}
